import type { Descendant, Text } from 'platejs';

type SlateText = Text & {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
};

type SlateElement = {
    type?: string;
    children: Descendant[];
    url?: string;
    colSpan?: number;
    rowSpan?: number;
    listStyleType?: string;
    indent?: number;
    listStart?: number;
    [key: string]: unknown;
};

const ULIST_STYLES = new Set(['disc', 'circle', 'square', 'disclosure-open', 'disclosure-closed']);

const escapeHtml = (text: string): string => {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

const serializeLeaf = (node: SlateText): string => {
    let text = escapeHtml(node.text).replace(/\n/g, '<br>');

    if (node.bold) {
        text = `<strong>${text}</strong>`;
    }
    if (node.italic) {
        text = `<em>${text}</em>`;
    }
    if (node.underline) {
        text = `<u>${text}</u>`;
    }
    if (node.strikethrough) {
        text = `<s>${text}</s>`;
    }

    return text;
};

const serializeNode = (node: Descendant): string => {
    if ('text' in node) {
        return serializeLeaf(node as SlateText);
    }

    const element = node as SlateElement;
    const children = element.children?.map(serializeNode).join('') ?? '';

    switch (element.type) {
        case 'p': {
            if (element.listStyleType) {
                // Plate's HTML deserializer only recognizes list items via these data attributes
                return `<li data-list-style-type="${escapeHtml(element.listStyleType)}" data-indent="${element.indent ?? 1}">${children}</li>`;
            }
            // Empty <p></p> is dropped when the HTML is deserialized again, losing blank lines
            return `<p>${children || '&nbsp;'}</p>`;
        }
        case 'h1':
            return `<h1>${children}</h1>`;
        case 'h2':
            return `<h2>${children}</h2>`;
        case 'h3':
            return `<h3>${children}</h3>`;
        case 'h4':
            return `<h4>${children}</h4>`;
        case 'h5':
            return `<h5>${children}</h5>`;
        case 'h6':
            return `<h6>${children}</h6>`;
        case 'blockquote':
            return `<blockquote>${children}</blockquote>`;
        case 'ul':
            return `<ul>${children}</ul>`;
        case 'ol':
            return `<ol>${children}</ol>`;
        case 'li':
            return `<li>${children}</li>`;
        case 'lic':
            return children;
        case 'a':
            return `<a href="${escapeHtml(element.url as string || '')}">${children}</a>`;
        case 'table':
            return `<table>${children}</table>`;
        case 'tr':
            return `<tr>${children}</tr>`;
        case 'td': {
            const attrs: string[] = [];
            if (element.colSpan && element.colSpan > 1) attrs.push(`colspan="${element.colSpan}"`);
            if (element.rowSpan && element.rowSpan > 1) attrs.push(`rowspan="${element.rowSpan}"`);
            return `<td${attrs.length ? ' ' + attrs.join(' ') : ''}>${children}</td>`;
        }
        case 'th': {
            const attrs: string[] = [];
            if (element.colSpan && element.colSpan > 1) attrs.push(`colspan="${element.colSpan}"`);
            if (element.rowSpan && element.rowSpan > 1) attrs.push(`rowspan="${element.rowSpan}"`);
            return `<th${attrs.length ? ' ' + attrs.join(' ') : ''}>${children}</th>`;
        }
        default:
            return children ? `<p>${children}</p>` : '';
    }
};

/**
 * Groups consecutive indent-based list items into <ul>/<ol> wrappers,
 * while serializing non-list nodes normally.
 */
export const serializeToHtml = (nodes: Descendant[]): string => {
    const parts: string[] = [];
    let i = 0;

    while (i < nodes.length) {
        const node = nodes[i] as SlateElement;

        if (!('text' in node) && node.listStyleType) {
            const listStyle = node.listStyleType;
            const indent = node.indent ?? 1;
            const isUnordered = ULIST_STYLES.has(listStyle);
            const tag = isUnordered ? 'ul' : 'ol';

            const items: string[] = [];
            while (i < nodes.length) {
                const current = nodes[i] as SlateElement;
                if ('text' in current || !current.listStyleType) break;

                const curIsUnordered = ULIST_STYLES.has(current.listStyleType);
                const curIndent = current.indent ?? 1;
                if (curIsUnordered !== isUnordered || curIndent !== indent) break;

                items.push(serializeNode(current));
                i++;
            }

            const startAttr = !isUnordered && node.listStart && node.listStart > 1
                ? ` start="${node.listStart}"`
                : '';
            parts.push(`<${tag}${startAttr}>${items.join('')}</${tag}>`);
        } else {
            parts.push(serializeNode(nodes[i]));
            i++;
        }
    }

    return parts.join('');
};
